import { getPalette, sampleGradient, type Palette } from '@wg/shared';
import { classifyBiome, type BiomeId } from '@wg/noise';
import type { TwoDMode } from '@/stores/viewStore.js';

// Re-exported for convenience so callers of paintHeightmapInto don't need to
// reach into the store. Canonical declaration lives in stores/viewStore.ts.
export type { TwoDMode };

const BIOME_COLORS: Record<BiomeId, [number, number, number]> = {
  ocean:    [15, 76, 117],
  beach:    [237, 201, 175],
  desert:   [237, 201, 81],
  grass:    [120, 168, 70],
  forest:   [34, 102, 55],
  tundra:   [200, 210, 215],
  mountain: [120, 110, 100],
  snow:     [240, 245, 250],
};

// 256-entry RGB LUT per palette, cached by palette.id. Replaces the per-pixel
// sampleGradient call in the hot paint loops (which allocates a [r,g,b] tuple
// and runs culori.parse/formatRgb on every invocation) with three indexed
// Uint8Array reads. Built on first use per palette; ~4.6KB total for the
// 6 bundled palettes.
const paletteLutCache = new Map<string, Uint8Array>();
function getPaletteLut(palette: Palette): Uint8Array {
  const cached = paletteLutCache.get(palette.id);
  if (cached !== undefined) return cached;
  const lut = new Uint8Array(256 * 3);
  for (let k = 0; k < 256; k++) {
    const [r, g, b] = sampleGradient(palette, k / 255);
    lut[k * 3] = r;
    lut[k * 3 + 1] = g;
    lut[k * 3 + 2] = b;
  }
  paletteLutCache.set(palette.id, lut);
  return lut;
}

export function paintHeightmapInto(
  imageData: ImageData,
  heights: Float32Array,
  resolution: number,
  mode: TwoDMode,
  paletteId: string,
  waterLevel: number,
): void {
  const palette = getPalette(paletteId) ?? getPalette('blue-red')!;
  const data = imageData.data;
  switch (mode) {
    case 'raw':      return paintRaw(data, heights, palette);
    case 'biome':    return paintBiomes(data, heights, resolution, waterLevel);
    case 'contour':  return paintContour(data, heights, resolution, palette);
    case 'shaded':   return paintShaded(data, heights, resolution, palette);
  }
  const _exhaustive: never = mode;
  throw new Error(`Unknown 2D mode: ${String(_exhaustive)}`);
}

function paintRaw(data: Uint8ClampedArray, h: Float32Array, palette: Palette): void {
  const lut = getPaletteLut(palette);
  for (let i = 0; i < h.length; i++) {
    const k = Math.floor(h[i]! * 255);
    const l = k * 3;
    const j = i << 2;
    data[j]   = lut[l]!;
    data[j+1] = lut[l+1]!;
    data[j+2] = lut[l+2]!;
    data[j+3] = 255;
  }
}

function paintBiomes(data: Uint8ClampedArray, h: Float32Array, n: number, waterLevel: number): void {
  // Derive per-pixel moisture and temperature so all eight biomes are
  // reachable. With classifyBiome's default moisture=temperature=0.5 the
  // mid-elevation branch collapses to grass everywhere, masking tundra,
  // desert, and forest.
  //
  //   Temperature = 1 - height    (simple elevation lapse; mountains cold,
  //                                lowlands warm — lets tundra appear in
  //                                the 0.7..0.8 height band)
  //   Moisture    = 0.5 + 0.35 sin(u·15) cos(v·12)  in normalized coords
  //                                (patchy rain bands, range ≈ [0.15, 0.85];
  //                                resolution-independent via u=x/n, v=y/n)
  //
  // Reuse one input object across the loop instead of allocating per pixel;
  // classifyBiome only reads the fields, doesn't retain the object.
  const input: { height: number; waterLevel: number; moisture: number; temperature: number } = {
    height: 0, waterLevel, moisture: 0, temperature: 0,
  };
  const invN = 1 / n;
  for (let y = 0; y < n; y++) {
    const v = y * invN;
    for (let x = 0; x < n; x++) {
      const u = x * invN;
      const i = y * n + x;
      const hi = h[i]!;
      input.height = hi;
      input.moisture = 0.5 + 0.35 * Math.sin(u * 15) * Math.cos(v * 12);
      input.temperature = 1 - hi;
      const biome = classifyBiome(input);
      const [r, g, b] = BIOME_COLORS[biome];
      const j = i << 2;
      data[j] = r; data[j+1] = g; data[j+2] = b; data[j+3] = 255;
    }
  }
}

function paintContour(data: Uint8ClampedArray, h: Float32Array, n: number, palette: Palette): void {
  const lut = getPaletteLut(palette);
  const LEVELS = 12;
  for (let i = 0; i < h.length; i++) {
    // Quantize to LEVELS bands, then normalize to [0, 1]. At h[i] === 1 exactly
    // this produces 12/11 ≈ 1.09, so clamp the LUT index at 255.
    const q = Math.floor(h[i]! * LEVELS) / (LEVELS - 1);
    const k = q >= 1 ? 255 : Math.floor(q * 255);
    const l = k * 3;
    const j = i << 2;
    data[j]   = lut[l]!;
    data[j+1] = lut[l+1]!;
    data[j+2] = lut[l+2]!;
    data[j+3] = 255;
  }
  // Draw ridges where quantized level changes compared to neighbours.
  // Loop bounds cover every cell with a valid right/down neighbour — start at 0
  // so the top row and left column get contour edges too.
  for (let y = 0; y < n - 1; y++) {
    for (let x = 0; x < n - 1; x++) {
      const i = y * n + x;
      const q = Math.floor(h[i]! * LEVELS);
      const qR = Math.floor(h[i + 1]! * LEVELS);
      const qD = Math.floor(h[i + n]! * LEVELS);
      if (q !== qR || q !== qD) {
        const j = i << 2; data[j] = 0; data[j+1] = 0; data[j+2] = 0;
      }
    }
  }
}

function paintShaded(data: Uint8ClampedArray, h: Float32Array, n: number, palette: Palette): void {
  // Simple hillshade: project gradient onto a synthetic sun direction.
  // The raw projection magnitude depends on how rough the heightmap is —
  // smooth fBm produces adjacent-cell deltas around 0.01–0.05, so a fixed
  // gain leaves most pixels near mid-shade and the result reads as a
  // uniformly darker raw mode. Normalize per paint so the full shade
  // range [0.2, 1] is always in play.
  const lut = getPaletteLut(palette);
  const sunX = -1, sunY = -1, invLen = 1 / Math.SQRT2;

  // Pass 1: find the maximum |gradient · sun| over the image.
  let maxAbs = 0;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = y * n + x;
      const hi = h[i]!;
      const hx = (x > 0 ? hi - h[i - 1]! : 0);
      const hy = (y > 0 ? hi - h[i - n]! : 0);
      const proj = (hx * sunX + hy * sunY) * invLen;
      const a = proj < 0 ? -proj : proj;
      if (a > maxAbs) maxAbs = a;
    }
  }
  // gain maps the largest projection to ±0.5 so shade lands in [0, 1] pre-clamp.
  // maxAbs === 0 only on a perfectly flat heightmap — fall back to flat shading.
  const gain = maxAbs > 0 ? 0.5 / maxAbs : 0;

  // Pass 2: paint with normalized shading.
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = y * n + x;
      const hi = h[i]!;
      const hx = (x > 0 ? hi - h[i - 1]! : 0);
      const hy = (y > 0 ? hi - h[i - n]! : 0);
      let shade = (hx * sunX + hy * sunY) * invLen * gain + 0.5;
      shade = shade < 0.2 ? 0.2 : shade > 1 ? 1 : shade;
      const k = Math.floor(hi * 255);
      const l = k * 3;
      const j = i << 2;
      data[j]   = lut[l]! * shade;
      data[j+1] = lut[l+1]! * shade;
      data[j+2] = lut[l+2]! * shade;
      data[j+3] = 255;
    }
  }
}
